import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import EprLineItem, { EPR_MAX_FILES_PER_KIND } from "./reusable/epr-line-item";
import ApiService from "../../api/apiService";
import showSwal from "../swal";
import {
  getCurrentDateToday,
  convertTo12HourPlusDateFormat,
  formatToPeso,
} from "../../util/helperFunctions";
// epr.css is kept because its classes are shared with other request forms.
import "../../styles/epr.css";
import "../../styles/epr-v2.css";

// The upload middleware only accepts files on the first 5 line items.
const MAX_ITEMS_WITH_FILES = 5;

let keySeed = 0;
const nextKey = () => `item-${Date.now()}-${keySeed++}`;

const emptyItem = () => ({
  key: nextKey(),
  purchaseId: null,
  description: "",
  remarks: "",
  quantity: "",
  price: "",
  attachments: [],
  files: [],
});

const todayIso = () => new Date().toISOString().split("T")[0];

const emptyForm = () => ({
  company_id: "",
  purchase_request_id: "",
  date_needed: getCurrentDateToday(),
  recommended_supplier: "",
  urgent: false,
  withholding_tax: "",
  items: [emptyItem()],
});

const mapServerFile = (f) => ({
  kind: "existing",
  id: f.id,
  name: f.attachment,
  url: f.file_path,
  type: f.file_type || "",
});

// Turns the requisition returned by getFormRequestDetail into the form's state.
export const mapRequisitionToForm = (requisition) => {
  const items = (requisition?.pr_purchases_detail || []).map((p) => ({
    key: nextKey(),
    purchaseId: p.id,
    description: p.description || "",
    remarks: p.remarks || "",
    quantity: p.quantity ? String(p.quantity) : "",
    price: p.estimated_price ? String(Number(p.estimated_price)) : "",
    attachments: (p.purchases_attachment_detail || []).map(mapServerFile),
    files: (p.purchases_files_detail || []).map(mapServerFile),
  }));

  return {
    requestId: requisition?.form_request_detail?.id,
    referenceId: requisition?.form_request_detail?.reference_id,
    status: requisition?.form_request_detail?.final_status,
    form: {
      company_id: requisition?.company_id ? String(requisition.company_id) : "",
      purchase_request_id: requisition?.purchase_request_id
        ? String(requisition.purchase_request_id)
        : "",
      date_needed: requisition?.date_needed
        ? String(requisition.date_needed).slice(0, 10)
        : getCurrentDateToday(),
      recommended_supplier: requisition?.recommended_supplier || "",
      urgent: Boolean(requisition?.urgent),
      withholding_tax:
        Number(requisition?.withholding_tax) > 0
          ? String(Number(requisition.withholding_tax))
          : "",
      items: items.length > 0 ? items : [emptyItem()],
    },
  };
};

const hasContent = (item) =>
  item.description.trim() !== "" ||
  item.remarks.trim() !== "" ||
  Number(item.quantity) > 0 ||
  Number(item.price) > 0 ||
  item.attachments.length > 0 ||
  item.files.length > 0;

// Comparable snapshot for the unsaved-changes guard (File objects aren't serialisable).
const snapshotOf = (form) =>
  JSON.stringify({
    ...form,
    items: form.items.map(({ key, attachments, files, ...rest }) => ({
      ...rest,
      attachments: attachments.map((f) => f.uid || f.id),
      files: files.map((f) => f.uid || f.id),
    })),
  });

const validate = (form) => {
  const errors = {};
  if (!form.company_id) errors.company_id = "Choose the company this is for.";
  if (!form.purchase_request_id)
    errors.purchase_request_id = "Choose a request type.";
  if (!form.date_needed) errors.date_needed = "Set the date you need this by.";
  else if (form.date_needed < todayIso())
    errors.date_needed = "Date needed can't be in the past.";

  form.items.forEach((item) => {
    if (!item.description.trim())
      errors[`items.${item.key}.description`] = "Describe the item.";
    if (!(Number(item.quantity) > 0))
      errors[`items.${item.key}.quantity`] = "Enter a quantity.";
  });
  if (form.items.length === 0) errors.items = "Add at least one item.";

  const withholdingTax = Number(form.withholding_tax) || 0;
  const subtotal = form.items.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.price) || 0),
    0,
  );
  if (withholdingTax > subtotal)
    errors.withholding_tax = "Withholding tax can't be more than the subtotal.";
  return errors;
};

const fieldIdForError = (errorKey) => {
  const map = {
    company_id: "prq-company",
    purchase_request_id: "prq-type",
    date_needed: "prq-date",
    withholding_tax: "prq-wht",
  };
  if (map[errorKey]) return map[errorKey];
  const [, key, field] = errorKey.split(".");
  return field === "quantity" ? `prq-qty-${key}` : `prq-desc-${key}`;
};

const buildEprFormData = (form, action) => {
  const fd = new FormData();
  fd.append("company_id", form.company_id);
  fd.append("recommended_supplier", form.recommended_supplier.trim());
  fd.append("purchase_request_id", form.purchase_request_id);
  fd.append("urgent", form.urgent);
  fd.append("date_needed", form.date_needed);
  fd.append("withholding_tax", Number(form.withholding_tax) || 0);
  if (action) fd.append("action", action);

  form.items.forEach((item, i) => {
    const quantity = Number(item.quantity) || 0;
    const price = Number(item.price) || 0;
    const prefix = `requisitions[${i}]`;

    fd.append(`${prefix}[id]`, item.key);
    if (item.purchaseId) fd.append(`${prefix}[purchase_id]`, item.purchaseId);
    fd.append(`${prefix}[description]`, item.description.trim());
    fd.append(`${prefix}[remarks]`, item.remarks.trim());
    fd.append(`${prefix}[quantity]`, quantity);
    fd.append(`${prefix}[price]`, price);
    fd.append(`${prefix}[cost]`, quantity * price);

    [
      ["attachments", "attachment", "keep_attachment_ids"],
      ["files", "file", "keep_file_ids"],
    ].forEach(([stateKey, fieldKey, keepKey]) => {
      const kept = item[stateKey].filter((f) => f.kind === "existing");
      const fresh = item[stateKey].filter((f) => f.kind === "new");

      kept.forEach((f, k) => fd.append(`${prefix}[${keepKey}][${k}]`, f.id));
      // New uploads take the slots after the kept files so the total stays within the limit.
      fresh.forEach((f, n) => {
        const slot = `${prefix}[${fieldKey}][${kept.length + n}]`;
        fd.append(`${slot}[filePreview]`, f.previewUrl);
        fd.append(`${slot}[file]`, f.file);
        fd.append(`${slot}[type]`, f.type);
        fd.append(`${slot}[size]`, f.size / 1024 / 1024);
      });
    });
  });

  return fd;
};

const formatSavedTime = (date) =>
  date.toLocaleTimeString("en-PH", { hour: "numeric", minute: "2-digit" });

function EprForm({
  formDetails,
  setFormSubmitted,
  setReferenceId,
  setDateAndTime,
  setSubmitOutcome = () => {},
  mode: initialMode = "new",
  initialRequest = null,
}) {
  const {
    id: formId,
    name: formName,
    purchase_requisition_company_detail = [],
    purchase_requisition_pr_detail = [],
  } = formDetails;

  const [mode, setMode] = useState(initialMode);
  const [requestId, setRequestId] = useState(initialRequest?.requestId || null);
  const [referenceId, setLocalReferenceId] = useState(
    initialRequest?.referenceId || "",
  );
  const [form, setForm] = useState(() => initialRequest?.form || emptyForm());
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    snapshotOf(initialRequest?.form || emptyForm()),
  );
  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(null); // "draft" | "submit" | null
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [removed, setRemoved] = useState(null); // { item, index } for undo

  const undoTimer = useRef(null);
  const reviewConfirmRef = useRef(null);

  const isDirty = snapshotOf(form) !== savedSnapshot;
  const isEdit = mode === "edit";
  const isBusy = busy !== null;

  // Live re-validation once the user has tried to submit.
  useEffect(() => {
    if (showErrors) setErrors(validate(form));
  }, [form, showErrors]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty || isBusy) return;
      event.preventDefault();
      event.returnValue =
        "You have unsaved changes. Are you sure you want to leave?";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, isBusy]);

  useEffect(() => () => clearTimeout(undoTimer.current), []);

  useEffect(() => {
    if (!reviewOpen) return undefined;
    reviewConfirmRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape" && !isBusy) setReviewOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [reviewOpen, isBusy]);

  const totals = useMemo(
    () =>
      form.items.reduce(
        (acc, item) => {
          const qty = Number(item.quantity) || 0;
          acc.quantity += qty;
          acc.cost += qty * (Number(item.price) || 0);
          acc.files += item.attachments.length + item.files.length;
          return acc;
        },
        { quantity: 0, cost: 0, files: 0 },
      ),
    [form.items],
  );
  const withholdingTax = Number(form.withholding_tax) || 0;
  const netTotal = totals.cost - withholdingTax;

  const companyName =
    purchase_requisition_company_detail.find(
      (c) => String(c.id) === String(form.company_id),
    )?.name || "";
  const typeName =
    purchase_requisition_pr_detail.find(
      (t) => String(t.id) === String(form.purchase_request_id),
    )?.name || "";

  const setField = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateItem = useCallback((key, patch) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.key === key ? { ...item, ...patch } : item,
      ),
    }));
  }, []);

  const addItem = () => {
    const item = emptyItem();
    setForm((prev) => ({ ...prev, items: [...prev.items, item] }));
    // Focus the new row's description once it renders.
    setTimeout(() => document.getElementById(`prq-desc-${item.key}`)?.focus(), 0);
  };

  const duplicateItem = (key) => {
    setForm((prev) => {
      const index = prev.items.findIndex((i) => i.key === key);
      const source = prev.items[index];
      // Files are not copied: each upload belongs to a single line.
      const copy = {
        ...emptyItem(),
        description: source.description,
        remarks: source.remarks,
        quantity: source.quantity,
        price: source.price,
      };
      const items = [...prev.items];
      items.splice(index + 1, 0, copy);
      return { ...prev, items };
    });
  };

  const removeItem = (key) => {
    const index = form.items.findIndex((i) => i.key === key);
    const item = form.items[index];
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((i) => i.key !== key),
    }));

    clearTimeout(undoTimer.current);
    if (hasContent(item)) {
      setRemoved({ item, index });
      undoTimer.current = setTimeout(() => {
        setRemoved((current) => {
          if (current?.item.key === item.key) {
            [...current.item.attachments, ...current.item.files].forEach(
              (f) => f.kind === "new" && URL.revokeObjectURL(f.previewUrl),
            );
            return null;
          }
          return current;
        });
      }, 6000);
    } else {
      setRemoved(null);
    }
  };

  const undoRemove = () => {
    if (!removed) return;
    clearTimeout(undoTimer.current);
    setForm((prev) => {
      const items = [...prev.items];
      items.splice(Math.min(removed.index, items.length), 0, removed.item);
      return { ...prev, items };
    });
    setRemoved(null);
  };

  const focusFirstError = (errs) => {
    const first = Object.keys(errs)[0];
    if (!first) return;
    const el = document.getElementById(
      first === "items" ? "prq-items" : fieldIdForError(first),
    );
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus({ preventScroll: true });
    }
  };

  const showServerError = (title, message) =>
    showSwal({
      title,
      icon: "error",
      text: message || "Please try again in a moment.",
      confirmButtonText: "OK",
    });

  // Reload server state so saved files/items carry their ids on the next save.
  const reloadFromServer = async (savedRequestId) => {
    const response = await ApiService.forms.getFormRequestDetail(
      "epr",
      savedRequestId,
    );
    if (!response?.success) return null;
    const mapped = mapRequisitionToForm(response.request?.requisition);
    setForm(mapped.form);
    setSavedSnapshot(snapshotOf(mapped.form));
    return mapped;
  };

  const handleSaveDraft = async () => {
    if (isBusy) return;
    setBusy("draft");
    try {
      const fd = buildEprFormData(form, requestId ? "save" : null);
      const response = requestId
        ? await ApiService.forms.updateEprRequest(requestId, fd)
        : await ApiService.forms.saveEprDraft(formId, fd);

      if (!response?.success) {
        showServerError("Draft not saved", response?.message);
        return;
      }

      const savedId = response.requisition?.id || requestId;
      if (!requestId && savedId) {
        setRequestId(savedId);
        setMode("draft");
        setLocalReferenceId(response.requisition?.reference_id || "");
        const params = new URLSearchParams(window.location.search);
        params.set("d", savedId);
        window.history.replaceState(
          null,
          "",
          `${window.location.pathname}?${params.toString()}`,
        );
      }

      const reloaded = await reloadFromServer(savedId);
      if (!reloaded) setSavedSnapshot(snapshotOf(form));
      setLastSavedAt(new Date());
    } catch (error) {
      showServerError("Draft not saved", error?.message);
    } finally {
      setBusy(null);
    }
  };

  const openReview = () => {
    const errs = validate(form);
    setShowErrors(true);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      focusFirstError(errs);
      return;
    }
    setReviewOpen(true);
  };

  const handleSubmit = async () => {
    if (isBusy) return;
    setBusy("submit");
    try {
      let response;
      if (isEdit) {
        response = await ApiService.forms.updateEprRequest(
          requestId,
          buildEprFormData(form, "save"),
        );
      } else if (requestId) {
        response = await ApiService.forms.updateEprRequest(
          requestId,
          buildEprFormData(form, "submit"),
        );
      } else {
        response = await ApiService.forms.submitFormRequest(
          formId,
          buildEprFormData(form),
        );
      }

      if (!response?.success) {
        showServerError(
          isEdit ? "Changes not saved" : "Request not submitted",
          response?.message,
        );
        return;
      }

      const { requisition } = response;
      setSavedSnapshot(snapshotOf(form));
      setSubmitOutcome(isEdit ? "updated" : "submitted");
      setReferenceId(requisition?.reference_id || referenceId);
      setDateAndTime(
        convertTo12HourPlusDateFormat(
          isEdit ? requisition?.updatedAt || new Date() : requisition?.createdAt,
        ),
      );
      setReviewOpen(false);
      setFormSubmitted(true);
    } catch (error) {
      showServerError(
        isEdit ? "Changes not saved" : "Request not submitted",
        error?.message,
      );
    } finally {
      setBusy(null);
    }
  };

  const errorCount = showErrors ? Object.keys(errors).length : 0;

  const heading = {
    new: "New purchase requisition",
    draft: "Draft requisition",
    edit: "Edit requisition",
  }[mode];

  const lede = {
    new: "List what you need and who it's for. Approvers see exactly what you enter here.",
    draft:
      "This draft is only visible to you. Submit it when it's ready for approval.",
    edit: "You can change this request until an approver acts on it. Approvers will see the updated version.",
  }[mode];

  const primaryLabel = isEdit ? "Review changes" : "Review & submit";

  const saveStatus = busy === "draft"
    ? "Saving…"
    : lastSavedAt && !isDirty
      ? `Draft saved · ${formatSavedTime(lastSavedAt)}`
      : isDirty && requestId && !isEdit
        ? "Unsaved changes"
        : "";

  const renderActions = (variant) => (
    <div className={`prq-actions prq-actions--${variant}`}>
      {variant === "bar" && (
        <div className="prq-actions__total">
          <span>Total</span>
          <strong>{formatToPeso(netTotal)}</strong>
        </div>
      )}
      <div className="prq-actions__buttons">
        {!isEdit && (
          <button
            type="button"
            className="prq-btn prq-btn--quiet"
            onClick={handleSaveDraft}
            disabled={isBusy}
          >
            {busy === "draft" ? "Saving…" : "Save draft"}
          </button>
        )}
        <button
          type="button"
          className="prq-btn prq-btn--primary"
          onClick={openReview}
          disabled={isBusy}
        >
          {primaryLabel}
        </button>
      </div>
      {variant === "ledger" && (saveStatus || errorCount > 0) && (
        <p className="prq-actions__status" aria-live="polite">
          {errorCount > 0 ? (
            <span className="prq-actions__errors">
              {errorCount} {errorCount === 1 ? "field needs" : "fields need"}{" "}
              attention
            </span>
          ) : (
            saveStatus
          )}
        </p>
      )}
    </div>
  );

  return (
    <div className="prq">
      <header className="prq-head">
        <div>
          <p className="prq-head__crumb">
            {formName || "Purchase requisition"}
            {referenceId && (
              <>
                <span aria-hidden="true"> / </span>
                <span className="prq-mono">{referenceId}</span>
              </>
            )}
          </p>
          <h1 className="prq-head__title">
            {heading}
            {mode === "draft" && <span className="prq-tag">Draft</span>}
            {isEdit && <span className="prq-tag prq-tag--edit">Pending</span>}
          </h1>
          <p className="prq-head__lede">{lede}</p>
        </div>
      </header>

      <div className="prq-layout">
        <div className="prq-sheet">
          <section className="prq-sec" aria-labelledby="prq-sec-request">
            <div className="prq-sec__aside">
              <h2 id="prq-sec-request">Request</h2>
              <p>Which company is buying, and what kind of purchase this is.</p>
            </div>
            <div className="prq-sec__body prq-grid-2">
              <div className="prq-field">
                <label htmlFor="prq-company">
                  Company <span className="prq-req">required</span>
                </label>
                <select
                  id="prq-company"
                  value={form.company_id}
                  onChange={setField("company_id")}
                  disabled={isBusy}
                  aria-invalid={Boolean(errors.company_id)}
                >
                  <option value="" disabled>
                    Select company
                  </option>
                  {purchase_requisition_company_detail.map((c) => (
                    <option value={String(c.id)} key={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                {errors.company_id && (
                  <p className="prq-error">{errors.company_id}</p>
                )}
              </div>

              <div className="prq-field">
                <label htmlFor="prq-type">
                  Type of request <span className="prq-req">required</span>
                </label>
                <select
                  id="prq-type"
                  value={form.purchase_request_id}
                  onChange={setField("purchase_request_id")}
                  disabled={isBusy}
                  aria-invalid={Boolean(errors.purchase_request_id)}
                >
                  <option value="" disabled>
                    Select type
                  </option>
                  {purchase_requisition_pr_detail.map((t) => (
                    <option value={String(t.id)} key={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
                {errors.purchase_request_id && (
                  <p className="prq-error">{errors.purchase_request_id}</p>
                )}
              </div>
            </div>
          </section>

          <section className="prq-sec" aria-labelledby="prq-sec-timing">
            <div className="prq-sec__aside">
              <h2 id="prq-sec-timing">Timing &amp; supplier</h2>
              <p>When you need it, and who you'd suggest buying from.</p>
            </div>
            <div className="prq-sec__body">
              <div className="prq-grid-2">
                <div className="prq-field">
                  <label htmlFor="prq-date">
                    Date needed <span className="prq-req">required</span>
                  </label>
                  <input
                    id="prq-date"
                    type="date"
                    min={todayIso()}
                    value={form.date_needed}
                    onChange={setField("date_needed")}
                    disabled={isBusy}
                    aria-invalid={Boolean(errors.date_needed)}
                  />
                  {errors.date_needed && (
                    <p className="prq-error">{errors.date_needed}</p>
                  )}
                </div>

                <div className="prq-field">
                  <label htmlFor="prq-supplier">
                    Recommended supplier <span className="prq-opt">optional</span>
                  </label>
                  <input
                    id="prq-supplier"
                    type="text"
                    value={form.recommended_supplier}
                    onChange={setField("recommended_supplier")}
                    disabled={isBusy}
                    placeholder="e.g. Wilcon Depot – Mandaue"
                    autoComplete="off"
                  />
                </div>
              </div>

              <label
                className={`prq-urgent${form.urgent ? " is-on" : ""}`}
                htmlFor="prq-urgent"
              >
                <input
                  id="prq-urgent"
                  type="checkbox"
                  role="switch"
                  checked={form.urgent}
                  onChange={setField("urgent")}
                  disabled={isBusy}
                />
                <span className="prq-urgent__track" aria-hidden="true"></span>
                <span className="prq-urgent__text">
                  <strong>Urgent</strong>
                  <small>
                    Flags the request for approvers. Use it only when waiting
                    would stop work.
                  </small>
                </span>
              </label>
            </div>
          </section>

          <section className="prq-sec prq-sec--items" aria-labelledby="prq-sec-items">
            <div className="prq-sec__aside">
              <h2 id="prq-sec-items">Items</h2>
              <p>
                One line per product or service. Attach quotes or photos to the
                line they belong to.
              </p>
            </div>
            <div className="prq-sec__body" id="prq-items" tabIndex={-1}>
              <div className="prq-items__head" aria-hidden="true">
                <span>#</span>
                <span>Description</span>
                <span>Qty</span>
                <span>Unit price</span>
                <span>Amount</span>
                <span></span>
              </div>

              {form.items.length === 0 ? (
                <p className="prq-items__empty">
                  No items yet. Add the first thing you need below.
                </p>
              ) : (
                form.items.map((item, index) => (
                  <EprLineItem
                    key={item.key}
                    item={item}
                    index={index}
                    errors={errors}
                    disabled={isBusy}
                    filesAllowed={index < MAX_ITEMS_WITH_FILES}
                    canRemove={form.items.length > 1 || hasContent(item)}
                    onChange={updateItem}
                    onRemove={removeItem}
                    onDuplicate={duplicateItem}
                  />
                ))
              )}

              {errors.items && <p className="prq-error">{errors.items}</p>}

              {removed && (
                <div className="prq-undo" role="status">
                  <span>
                    Removed “{removed.item.description.trim() || `item ${removed.index + 1}`}”
                  </span>
                  <button type="button" className="prq-linkbtn" onClick={undoRemove}>
                    Undo
                  </button>
                </div>
              )}

              <div className="prq-items__foot">
                <button
                  type="button"
                  className="prq-btn prq-btn--add"
                  onClick={addItem}
                  disabled={isBusy}
                >
                  <i className="fa fa-plus" aria-hidden="true"></i>
                  Add another item
                </button>
                <span className="prq-items__subtotal">
                  Subtotal <strong>{formatToPeso(totals.cost)}</strong>
                </span>
              </div>
              {form.items.length > MAX_ITEMS_WITH_FILES && (
                <p className="prq-hint">
                  Files can be attached to the first {MAX_ITEMS_WITH_FILES} items
                  only (up to {EPR_MAX_FILES_PER_KIND} of each type per item).
                </p>
              )}

              <div className="prq-wht">
                <div className="prq-field">
                  <label htmlFor="prq-wht">
                    Withholding tax <span className="prq-opt">optional</span>
                  </label>
                  <input
                    id="prq-wht"
                    type="text"
                    inputMode="decimal"
                    value={form.withholding_tax}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw))
                        setForm((prev) => ({ ...prev, withholding_tax: raw }));
                    }}
                    disabled={isBusy}
                    placeholder="0.00"
                    aria-invalid={Boolean(errors.withholding_tax)}
                  />
                  {errors.withholding_tax && (
                    <p className="prq-error">{errors.withholding_tax}</p>
                  )}
                </div>
                <span className="prq-items__subtotal">
                  Total <strong>{formatToPeso(netTotal)}</strong>
                </span>
              </div>
              <p className="prq-hint">
                Deducted from the subtotal. Leave blank if there's none.
              </p>
            </div>
          </section>
        </div>

        <aside className="prq-ledger" aria-label="Request totals">
          <dl className="prq-ledger__rows">
            <div>
              <dt>Company</dt>
              <dd>{companyName || "—"}</dd>
            </div>
            <div>
              <dt>Type</dt>
              <dd>{typeName || "—"}</dd>
            </div>
            <div>
              <dt>Items</dt>
              <dd>{form.items.length}</dd>
            </div>
            <div>
              <dt>Total quantity</dt>
              <dd>{totals.quantity.toLocaleString("en-PH")}</dd>
            </div>
            <div>
              <dt>Files</dt>
              <dd>{totals.files}</dd>
            </div>
            {withholdingTax > 0 && (
              <>
                <div>
                  <dt>Subtotal</dt>
                  <dd>{formatToPeso(totals.cost)}</dd>
                </div>
                <div>
                  <dt>Withholding tax</dt>
                  <dd>−{formatToPeso(withholdingTax)}</dd>
                </div>
              </>
            )}
          </dl>
          <div className="prq-ledger__total">
            <span>Estimated total</span>
            <strong>{formatToPeso(netTotal)}</strong>
          </div>
          {form.urgent && <p className="prq-ledger__flag">Marked urgent</p>}
          {renderActions("ledger")}
        </aside>
      </div>

      {renderActions("bar")}

      {reviewOpen && (
        <div
          className="prq-review"
          role="dialog"
          aria-modal="true"
          aria-labelledby="prq-review-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !isBusy) setReviewOpen(false);
          }}
        >
          <div className="prq-review__panel">
            <div className="prq-review__head">
              <h2 id="prq-review-title">
                {isEdit ? "Save these changes?" : "Submit for approval?"}
              </h2>
              <button
                type="button"
                className="prq-iconbtn"
                onClick={() => setReviewOpen(false)}
                disabled={isBusy}
                aria-label="Close review"
              >
                <i className="fa fa-times" aria-hidden="true"></i>
              </button>
            </div>

            <dl className="prq-review__facts">
              <div>
                <dt>Company</dt>
                <dd>{companyName}</dd>
              </div>
              <div>
                <dt>Type</dt>
                <dd>{typeName}</dd>
              </div>
              <div>
                <dt>Needed by</dt>
                <dd>
                  {new Date(`${form.date_needed}T00:00:00`).toLocaleDateString(
                    "en-PH",
                    { month: "short", day: "numeric", year: "numeric" },
                  )}
                  {form.urgent && <span className="prq-tag prq-tag--urgent">Urgent</span>}
                </dd>
              </div>
              {form.recommended_supplier.trim() && (
                <div>
                  <dt>Supplier</dt>
                  <dd>{form.recommended_supplier.trim()}</dd>
                </div>
              )}
            </dl>

            <ol className="prq-review__items">
              {form.items.map((item) => {
                const qty = Number(item.quantity) || 0;
                const amount = qty * (Number(item.price) || 0);
                const fileCount = item.attachments.length + item.files.length;
                return (
                  <li key={item.key}>
                    <span className="prq-review__desc">
                      {item.description.trim()}
                      <small>
                        {qty.toLocaleString("en-PH")} ×{" "}
                        {formatToPeso(Number(item.price) || 0)}
                        {fileCount > 0 &&
                          ` · ${fileCount} file${fileCount > 1 ? "s" : ""}`}
                      </small>
                    </span>
                    <span className="prq-mono">{formatToPeso(amount)}</span>
                  </li>
                );
              })}
              {withholdingTax > 0 && (
                <li>
                  <span className="prq-review__desc">Withholding tax</span>
                  <span className="prq-mono">−{formatToPeso(withholdingTax)}</span>
                </li>
              )}
            </ol>

            <div className="prq-review__total">
              <span>Estimated total</span>
              <strong>{formatToPeso(netTotal)}</strong>
            </div>

            <div className="prq-review__actions">
              <button
                type="button"
                className="prq-btn prq-btn--quiet"
                onClick={() => setReviewOpen(false)}
                disabled={isBusy}
              >
                Keep editing
              </button>
              <button
                type="button"
                ref={reviewConfirmRef}
                className="prq-btn prq-btn--primary"
                onClick={handleSubmit}
                disabled={isBusy}
                aria-busy={busy === "submit"}
              >
                {busy === "submit"
                  ? isEdit
                    ? "Saving…"
                    : "Submitting…"
                  : isEdit
                    ? "Save changes"
                    : "Submit for approval"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EprForm;
